import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FormData } from '../types';
import { PREDEFINED_TAGS } from '../../../../pages/DesignSystem/types/tags.types';
import { cloudinaryService } from '../../../../services/cloudinary.service';
import { supabase } from '../../../../config/supabase';
import {
  Textarea,
  MultiPriceInput,
  FileInput,
  TagList,
  ContentBlock,
  Input
} from '../../../../pages/DesignSystem';
import Button from '../../../../pages/DesignSystem/primitives/Button';
import { Icon } from '../../../../pages/DesignSystem';
import '../../../../styles/form-components.css';

// Timezone prefix → currency code mapping
const TIMEZONE_TO_CURRENCY: Record<string, string> = {
  'Asia/Colombo': 'LKR',
  'Asia/Kolkata': 'INR', 'Asia/Calcutta': 'INR',
  'Asia/Bangkok': 'THB',
  'Asia/Jakarta': 'IDR',
  'Asia/Singapore': 'SGD',
  'Asia/Kuala_Lumpur': 'MYR',
  'Asia/Ho_Chi_Minh': 'VND', 'Asia/Saigon': 'VND',
  'Asia/Tokyo': 'JPY',
  'Asia/Seoul': 'KSW',
  'Asia/Shanghai': 'CNY', 'Asia/Hong_Kong': 'HKD',
  'Asia/Dubai': 'AED', 'Asia/Riyadh': 'SAR',
  'Europe/London': 'GBP',
  'Europe/Zurich': 'CHF',
  'Europe/Stockholm': 'SEK',
  'Europe/Oslo': 'NOK',
  'Europe/Copenhagen': 'DKK',
  'Europe/Warsaw': 'PLN',
  'America/New_York': 'USD', 'America/Chicago': 'USD', 'America/Denver': 'USD', 'America/Los_Angeles': 'USD',
  'America/Toronto': 'CAD', 'America/Vancouver': 'CAD',
  'America/Mexico_City': 'MXN',
  'America/Sao_Paulo': 'BRL',
  'America/Argentina/Buenos_Aires': 'ARS',
  'Australia/Sydney': 'AUD', 'Australia/Melbourne': 'AUD', 'Australia/Perth': 'AUD',
  'Pacific/Auckland': 'NZD',
  'Africa/Johannesburg': 'ZAR',
  'Africa/Cairo': 'EGP',
  'Africa/Casablanca': 'MAD',
};

/** Get currency code from IANA timezone, fallback to USD */
function getCurrencyFromTimezone(timezone: string): string {
  if (!timezone) return 'USD';
  // Direct match
  if (TIMEZONE_TO_CURRENCY[timezone]) return TIMEZONE_TO_CURRENCY[timezone];
  // Europe/* defaults to EUR (except specific ones above)
  if (timezone.startsWith('Europe/')) return 'EUR';
  // America/* defaults to USD
  if (timezone.startsWith('America/')) return 'USD';
  return 'USD';
}

type AIImageState =
  | { status: 'idle' }
  | { status: 'generating' }
  | { status: 'error'; message: string }
  | { status: 'used' };

interface DetailsStepProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: Partial<Record<keyof FormData, string>>;
  handleTagToggle: (tagId: string) => void;
  onImageUploading?: (uploading: boolean) => void;
}

const DetailsStep: React.FC<DetailsStepProps> = ({
  formData,
  setFormData,
  errors,
  handleTagToggle,
  onImageUploading
}) => {
  // Auto-detect currency from event timezone, fallback to USD
  const currencyInitialized = useRef(false);
  useEffect(() => {
    if (currencyInitialized.current) return;
    currencyInitialized.current = true;

    const detectedCurrency = getCurrencyFromTimezone(formData.timezone);
    const currentPrices = formData.prices;

    if (!currentPrices || currentPrices.length === 0) {
      // No prices yet — initialize with detected currency
      setFormData(prev => ({
        ...prev,
        prices: [{ amount: 0, currency: detectedCurrency, description: 'Standard price' }]
      }));
    } else if (currentPrices.length === 1 && currentPrices[0].amount === 0 && (currentPrices[0].currency === 'EUR' || currentPrices[0].currency === 'USD')) {
      // Default untouched price — update currency to match location
      setFormData(prev => ({
        ...prev,
        prices: [{ ...prev.prices![0], currency: detectedCurrency }]
      }));
    }
  }, []); // Only runs when component mounts

  const [aiImageState, setAiImageState] = useState<AIImageState>({ status: 'idle' });

  const [isImageUploading, setIsImageUploading] = useState(false);

  const handleImageUpload = (files: FileList | null) => {
    const file = files?.[0];
    if (file) {
      // Create a temporary URL for preview
      const tempUrl = URL.createObjectURL(file);
      setFormData((prev: FormData) => ({ ...prev, image: tempUrl }));

      // Signal parent that upload is in progress
      setIsImageUploading(true);
      onImageUploading?.(true);

      // Upload to Cloudinary
      cloudinaryService.uploadImage(file)
        .then(cloudinaryUrl => {
          if (cloudinaryUrl) {
            setFormData(prev => ({ ...prev, image: cloudinaryUrl }));
            console.log('Image uploaded to Cloudinary:', cloudinaryUrl);
          } else {
            console.error('No URL received from Cloudinary');
            setFormData(prev => ({ ...prev, image: '' }));
          }
          URL.revokeObjectURL(tempUrl);
        })
        .catch(error => {
          console.error('Error uploading image:', error);
          setFormData(prev => ({ ...prev, image: '' }));
          URL.revokeObjectURL(tempUrl);
        })
        .finally(() => {
          setIsImageUploading(false);
          onImageUploading?.(false);
        });
    }
  };

  // AI image generation — one free generation, non-blocking
  const handleGenerateAI = useCallback(async () => {
    const title = formData.title?.trim();
    const description = formData.description?.trim();
    if (!title && !description) return;

    setAiImageState({ status: 'generating' });
    try {
      const sceneDescription = `${title || 'Event'}. ${description || ''}`.trim();

      const { data, error } = await supabase.functions.invoke('generate-insta-image', {
        body: {
          sceneDescription,
          tags: formData.tags || [],
        },
      });

      if (error) throw error;
      if (!data?.image) throw new Error('No image returned');

      // Convert base64 to File and inject into same upload flow
      const byteChars = atob(data.image);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }
      const mimeType = data.mimeType || 'image/jpeg';
      const blob = new Blob([byteArray], { type: mimeType });
      const file = new File([blob], 'ai-generated-cover.jpg', { type: mimeType });

      // Use same Cloudinary upload flow as manual upload
      const tempUrl = URL.createObjectURL(file);
      setFormData((prev: FormData) => ({ ...prev, image: tempUrl }));

      const cloudinaryUrl = await cloudinaryService.uploadImage(file);
      if (cloudinaryUrl) {
        setFormData(prev => ({ ...prev, image: cloudinaryUrl }));
      }
      URL.revokeObjectURL(tempUrl);

      setAiImageState({ status: 'used' });
    } catch (err) {
      console.error('AI image generation failed:', err);
      setAiImageState({ status: 'error', message: 'Generation failed. You can still upload a photo manually.' });
    }
  }, [formData.title, formData.description, formData.tags, setFormData]);

  const handleRemoveImage = useCallback(() => {
    setFormData(prev => ({ ...prev, image: '' }));
  }, [setFormData]);

  return (
    <div className="event-form__step-content">
      {/* Description Section */}
      <ContentBlock
        title="Event Description"
        spacing="md"
        border="light"
      >
        <div className="event-form__group">
          <label className="event-form__label event-form__label--required">
            <span className="event-form__label-hint">
              (Tell people what makes your event special!)
            </span>
          </label>
          <Textarea
            id="description"
            name="description"
            value={formData.description || ''}
            onChange={(value) => setFormData((prev: FormData) => ({ ...prev, description: value }))}
            placeholder="Example: Join us for an amazing evening of... We'll have... Don't miss out on..."
            error={errors.description}
            rows={4}
            required
            helper="💡 Tip: Include what people can expect, what to bring, and why they should come. The more details, the better!"
          />
        </div>
      </ContentBlock>

      {/* Price Section */}
      <ContentBlock
        title="Event Price"
        border="light"
      >
        <div className="event-form__group">
          <label className="event-form__label">
            <span className="event-form__label-hint">
              Leave it at 0 for a free event, or add up to 3 prices (e.g., Early Bird, Standard, VIP)
            </span>
          </label>
          <MultiPriceInput
            id="event-prices"
            prices={formData.prices || [{ amount: 0, currency: 'USD', description: 'Standard price' }]}
            onChange={(newPrices) => {
              // Accepter tous les prix pendant l'édition, sans déduplication
              setFormData(prev => ({
                ...prev,
                prices: newPrices.map(price => ({
                  amount: typeof price.amount === 'string' ? parseFloat(price.amount) : price.amount,
                  currency: price.currency,
                  description: price.description || 'Standard price'
                }))
              }));
            }}
            error={errors.prices}
          />
        </div>

        {/* Section Réservation */}
        <div className="event-form__group">
          <label className="event-form__label">
            <span className="event-form__label-hint">
              (How can people book or reserve their spot?)
            </span>
          </label>
          <Input
            id="booking-info"
            name="ticketingUrl"
            type="text"
            value={formData.ticketingUrl || ''}
            onChange={(value) => setFormData(prev => ({
              ...prev,
              ticketingUrl: value
            }))}
            placeholder="Website, WhatsApp number, or booking instructions"
            helper="💡 Add your Instagram, a link, or phone number — it will be shown on the event page!"
            icon="Ticket"
          />
        </div>
      </ContentBlock>

      {/* Image Section */}
      <ContentBlock
        title="Event Image"
        spacing="md"
        border="light"
      >
        <div className="event-form__group">
          <label className="event-form__label">
            <span className="event-form__label-hint">
              (A picture is worth a thousand words!)
            </span>
          </label>

          {/* Show FileInput + AI option when no image yet */}
          {!formData.image && (
            <>
              <FileInput
                id="event-image"
                name="image"
                accept="image/*"
                onChange={handleImageUpload}
                helper="💡 Tip: Use a clear, attractive image that represents your event. Best size is 4:5 (portrait, like an Instagram post). Maximum 5MB."
                dragAndDrop
              />

              {/* AI generation option — only if not already used */}
              {aiImageState.status !== 'used' && aiImageState.status !== 'generating' && (
                <div className="event-form__ai-image">
                  <div className="event-form__ai-image-separator">
                    <span>or</span>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={handleGenerateAI}
                    disabled={!formData.title?.trim() && !formData.description?.trim()}
                    className="event-form__ai-image-btn"
                  >
                    <Icon name="Sparkles" size={16} />
                    Generate with AI
                  </Button>
                  <p className="event-form__ai-image-hint">
                    One free generation — uses your event title & description
                  </p>
                  {aiImageState.status === 'error' && (
                    <p className="event-form__ai-image-error">{aiImageState.message}</p>
                  )}
                </div>
              )}

              {/* Generating state — non-blocking, inline indicator */}
              {aiImageState.status === 'generating' && (
                <div className="event-form__ai-image-loading">
                  <div className="event-form__ai-image-spinner" />
                  <span>Generating your image...</span>
                </div>
              )}
            </>
          )}

          {/* Image preview — shown when image exists (manual or AI) */}
          {formData.image && (
            <div className="event-form__image-preview-wrapper">
              <div className="event-form__image-preview">
                <img src={formData.image} alt="Preview" />
                {isImageUploading && (
                  <div className="event-form__image-uploading-overlay">
                    <div className="event-form__ai-image-spinner" />
                    <span>Uploading...</span>
                  </div>
                )}
              </div>
              <div className="event-form__image-preview-actions">
                <Button variant="ghost" onClick={handleRemoveImage} className="event-form__image-remove-btn" disabled={isImageUploading}>
                  <Icon name="X" size={14} />
                  Remove
                </Button>
                {aiImageState.status === 'used' && (
                  <span className="event-form__ai-image-badge">
                    <Icon name="Sparkles" size={12} />
                    AI generated
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </ContentBlock>

      {/* Categories Section */}
      <ContentBlock
        title="Event Categories"
        border="light"
      >
        <div className="event-form__group">
          <label className="event-form__label">
            <span className="event-form__label-hint">
              (Help people find your event!)
            </span>
          </label>
          <TagList
            tags={PREDEFINED_TAGS.filter(tag => tag.id !== 'favorites' && tag.id !== 'free')}
            selectedTags={formData.tags || []}
            onTagSelect={handleTagToggle}
            maxTags={3}
            onMaxTagsReached={() => {
              console.warn('Maximum 3 tags allowed');
            }}
          />
          <p className="input-helper">
            💡 Tip: Choose up to 3 categories that best describe your event. This helps people discover it when searching!
          </p>
        </div>
      </ContentBlock>
    </div>
  );
};

export default DetailsStep;
